import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { ProtectedRoute } from '@/components/permissions/ProtectedRoute';
import { Can } from '@/components/permissions/Can';
import { PERMISSIONS } from '@/lib/constants/permissions';
import { PageLoader } from '@/components/feedback/PageLoader';
import { ErrorFallback } from '@/components/feedback/ErrorFallback';

const LoginPage = lazy(() =>
  import('@/features/auth/pages/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const DashboardPage = lazy(() =>
  import('@/features/dashboard/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const UserListPage = lazy(() =>
  import('@/features/users/pages/UserListPage').then((m) => ({ default: m.UserListPage })),
);
const UserDetailPage = lazy(() =>
  import('@/features/users/pages/UserDetailPage').then((m) => ({ default: m.UserDetailPage })),
);
const CategoryListPage = lazy(() =>
  import('@/features/categories/pages/CategoryListPage').then((m) => ({
    default: m.CategoryListPage,
  })),
);
const CategoryDetailPage = lazy(() =>
  import('@/features/categories/pages/CategoryDetailPage').then((m) => ({
    default: m.CategoryDetailPage,
  })),
);
const PostListPage = lazy(() =>
  import('@/features/content/pages/PostListPage').then((m) => ({ default: m.PostListPage })),
);
const PostDetailPage = lazy(() =>
  import('@/features/content/pages/PostDetailPage').then((m) => ({ default: m.PostDetailPage })),
);
const ReportedPostsPage = lazy(() =>
  import('@/features/content/pages/ReportedPostsPage').then((m) => ({
    default: m.ReportedPostsPage,
  })),
);
const TrendingPostsPage = lazy(() =>
  import('@/features/content/pages/TrendingPostsPage').then((m) => ({
    default: m.TrendingPostsPage,
  })),
);
const FilterAnalyticsPage = lazy(() =>
  import('@/features/content/pages/FilterAnalyticsPage').then((m) => ({
    default: m.FilterAnalyticsPage,
  })),
);
const RatingsOverviewPage = lazy(() =>
  import('@/features/ratings/pages/RatingsOverviewPage').then((m) => ({
    default: m.RatingsOverviewPage,
  })),
);
const TribeListPage = lazy(() =>
  import('@/features/tribes/pages/TribeListPage').then((m) => ({ default: m.TribeListPage })),
);
const TribeDetailPage = lazy(() =>
  import('@/features/tribes/pages/TribeDetailPage').then((m) => ({ default: m.TribeDetailPage })),
);
const MessagingOversightPage = lazy(() =>
  import('@/features/messaging/pages/MessagingOversightPage').then((m) => ({
    default: m.MessagingOversightPage,
  })),
);
const ReportsDashboardPage = lazy(() =>
  import('@/features/reports/pages/ReportsDashboardPage').then((m) => ({
    default: m.ReportsDashboardPage,
  })),
);
const ReportDetailPage = lazy(() =>
  import('@/features/reports/pages/ReportDetailPage').then((m) => ({
    default: m.ReportDetailPage,
  })),
);
const NotificationTemplatesPage = lazy(() =>
  import('@/features/notifications/pages/NotificationTemplatesPage').then((m) => ({
    default: m.NotificationTemplatesPage,
  })),
);
const BroadcastPage = lazy(() =>
  import('@/features/notifications/pages/BroadcastPage').then((m) => ({
    default: m.BroadcastPage,
  })),
);
const DeliveryLogsPage = lazy(() =>
  import('@/features/notifications/pages/DeliveryLogsPage').then((m) => ({
    default: m.DeliveryLogsPage,
  })),
);
const NotificationStatsPage = lazy(() =>
  import('@/features/notifications/pages/NotificationStatsPage').then((m) => ({
    default: m.NotificationStatsPage,
  })),
);
const AnalyticsDashboardPage = lazy(() =>
  import('@/features/analytics/pages/AnalyticsDashboardPage').then((m) => ({
    default: m.AnalyticsDashboardPage,
  })),
);
const PolicyListPage = lazy(() =>
  import('@/features/policies/pages/PolicyListPage').then((m) => ({ default: m.PolicyListPage })),
);
const PolicyEditorPage = lazy(() =>
  import('@/features/policies/pages/PolicyEditorPage').then((m) => ({
    default: m.PolicyEditorPage,
  })),
);
const PolicyPreviewPage = lazy(() =>
  import('@/features/policies/pages/PolicyPreviewPage').then((m) => ({
    default: m.PolicyPreviewPage,
  })),
);
const AdminAuditPage = lazy(() =>
  import('@/features/audit-logs/pages/AdminAuditPage').then((m) => ({
    default: m.AdminAuditPage,
  })),
);
const UserAuditPage = lazy(() =>
  import('@/features/audit-logs/pages/UserAuditPage').then((m) => ({ default: m.UserAuditPage })),
);
const AdminProfilePage = lazy(() =>
  import('@/features/admin-profile/pages/AdminProfilePage').then((m) => ({
    default: m.AdminProfilePage,
  })),
);
const TicketListPage = lazy(() =>
  import('@/features/support/pages/TicketListPage').then((m) => ({ default: m.TicketListPage })),
);
const TicketDetailPage = lazy(() =>
  import('@/features/support/pages/TicketDetailPage').then((m) => ({
    default: m.TicketDetailPage,
  })),
);
const AdminListPage = lazy(() =>
  import('@/features/admins/pages/AdminListPage').then((m) => ({ default: m.AdminListPage })),
);
const AdminDetailPage = lazy(() =>
  import('@/features/admins/pages/AdminDetailPage').then((m) => ({ default: m.AdminDetailPage })),
);
const RoleListPage = lazy(() =>
  import('@/features/admins/pages/RoleListPage').then((m) => ({ default: m.RoleListPage })),
);
const RoleDetailPage = lazy(() =>
  import('@/features/admins/pages/RoleDetailPage').then((m) => ({ default: m.RoleDetailPage })),
);
const AdminOnboardingPage = lazy(() =>
  import('@/features/auth/pages/AdminOnboardingPage').then((m) => ({
    default: m.AdminOnboardingPage,
  })),
);
const AgeConfigPage = lazy(() =>
  import('@/features/settings/pages/AgeConfigPage').then((m) => ({ default: m.AgeConfigPage })),
);
const FeedRankingConfigPage = lazy(() =>
  import('@/features/settings/pages/FeedRankingConfigPage').then((m) => ({
    default: m.FeedRankingConfigPage,
  })),
);
const LikedPlacesPage = lazy(() =>
  import('@/features/location-likes/pages/LikedPlacesPage').then((m) => ({
    default: m.LikedPlacesPage,
  })),
);
const MapDiscoveryConfigPage = lazy(() =>
  import('@/features/settings/pages/MapDiscoveryConfigPage').then((m) => ({
    default: m.MapDiscoveryConfigPage,
  })),
);
const LocationLikeConfigPage = lazy(() =>
  import('@/features/settings/pages/LocationLikeConfigPage').then((m) => ({
    default: m.LocationLikeConfigPage,
  })),
);
const CheckInConfigPage = lazy(() =>
  import('@/features/settings/pages/CheckInConfigPage').then((m) => ({
    default: m.CheckInConfigPage,
  })),
);
const CountryRegionPage = lazy(() =>
  import('@/features/settings/pages/CountryRegionPage').then((m) => ({
    default: m.CountryRegionPage,
  })),
);
const LocationGovernancePage = lazy(() =>
  import('@/features/location/pages/LocationGovernancePage').then((m) => ({
    default: m.LocationGovernancePage,
  })),
);
const ChallengeListPage = lazy(() =>
  import('@/features/challenges/pages/ChallengeListPage').then((m) => ({
    default: m.ChallengeListPage,
  })),
);
const ChallengeFormPage = lazy(() =>
  import('@/features/challenges/pages/ChallengeFormPage').then((m) => ({
    default: m.ChallengeFormPage,
  })),
);
const GpsLocationListPage = lazy(() =>
  import('@/features/challenges/pages/GpsLocationListPage').then((m) => ({
    default: m.GpsLocationListPage,
  })),
);
const SubmissionListPage = lazy(() =>
  import('@/features/challenges/pages/SubmissionListPage').then((m) => ({
    default: m.SubmissionListPage,
  })),
);
const BannerListPage = lazy(() =>
  import('@/features/banners/pages/BannerListPage').then((m) => ({
    default: m.BannerListPage,
  })),
);
const ReferralCodeListPage = lazy(() =>
  import('@/features/referrals/pages/ReferralCodeListPage').then((m) => ({
    default: m.ReferralCodeListPage,
  })),
);
const ReferralCodeDetailPage = lazy(() =>
  import('@/features/referrals/pages/ReferralCodeDetailPage').then((m) => ({
    default: m.ReferralCodeDetailPage,
  })),
);
const WalletOverviewPage = lazy(() =>
  import('@/features/challenges/pages/WalletOverviewPage').then((m) => ({
    default: m.WalletOverviewPage,
  })),
);
const PayoutListPage = lazy(() =>
  import('@/features/challenges/pages/PayoutListPage').then((m) => ({
    default: m.PayoutListPage,
  })),
);
const ChallengeDetailPage = lazy(() =>
  import('@/features/challenges/pages/ChallengeDetailPage').then((m) => ({
    default: m.ChallengeDetailPage,
  })),
);
const BankAccountListPage = lazy(() =>
  import('@/features/challenges/pages/BankAccountListPage').then((m) => ({
    default: m.BankAccountListPage,
  })),
);
const MapLabPage = lazy(() =>
  import('@/features/map-lab/pages/MapLabPage').then((m) => ({
    default: m.MapLabPage,
  })),
);

const TransactionListPage = lazy(() =>
  import('@/features/challenges/pages/TransactionListPage').then((m) => ({
    default: m.TransactionListPage,
  })),
);

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <AuthLayout />,
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <LoginPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: '/onboarding',
    element: <AuthLayout />,
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <AdminOnboardingPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorFallback />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      {
        path: 'dashboard',
        element: (
          <SuspenseWrapper>
            <DashboardPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'users',
        element: (
          <SuspenseWrapper>
            <UserListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'users/:userId',
        element: (
          <SuspenseWrapper>
            <UserDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'categories',
        element: (
          <SuspenseWrapper>
            <CategoryListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'categories/:id',
        element: (
          <SuspenseWrapper>
            <CategoryDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'content/posts',
        element: (
          <SuspenseWrapper>
            <PostListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'content/posts/:postId',
        element: (
          <SuspenseWrapper>
            <PostDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'content/trending',
        element: (
          <SuspenseWrapper>
            <TrendingPostsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'content/reported',
        element: (
          <SuspenseWrapper>
            <ReportedPostsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'content/filter-analytics',
        element: (
          <SuspenseWrapper>
            <FilterAnalyticsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'ratings',
        element: (
          <SuspenseWrapper>
            <RatingsOverviewPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'tribes',
        element: (
          <SuspenseWrapper>
            <TribeListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'tribes/:tribeId',
        element: (
          <SuspenseWrapper>
            <TribeDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'messaging',
        element: (
          <SuspenseWrapper>
            <MessagingOversightPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports',
        element: (
          <SuspenseWrapper>
            <ReportsDashboardPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/:reportId',
        element: (
          <SuspenseWrapper>
            <ReportDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'notifications/templates',
        element: (
          <SuspenseWrapper>
            <NotificationTemplatesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'notifications/broadcast',
        element: (
          <SuspenseWrapper>
            <BroadcastPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'notifications/logs',
        element: (
          <SuspenseWrapper>
            <DeliveryLogsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'notifications/stats',
        element: (
          <SuspenseWrapper>
            <NotificationStatsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'analytics',
        element: (
          <SuspenseWrapper>
            <AnalyticsDashboardPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'policies',
        element: (
          <SuspenseWrapper>
            <PolicyListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'policies/new',
        element: (
          <SuspenseWrapper>
            <PolicyEditorPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'policies/:id/edit',
        element: (
          <SuspenseWrapper>
            <PolicyEditorPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'audit/admin',
        element: (
          <SuspenseWrapper>
            <AdminAuditPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'audit/user',
        element: (
          <SuspenseWrapper>
            <UserAuditPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'profile',
        element: (
          <SuspenseWrapper>
            <AdminProfilePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'support',
        element: (
          <SuspenseWrapper>
            <TicketListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'support/:ticketId',
        element: (
          <SuspenseWrapper>
            <TicketDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'admins',
        element: (
          <SuspenseWrapper>
            <AdminListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'admins/:adminId',
        element: (
          <SuspenseWrapper>
            <AdminDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'roles',
        element: (
          <SuspenseWrapper>
            <RoleListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'roles/:roleId',
        element: (
          <SuspenseWrapper>
            <RoleDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'settings/age-config',
        element: (
          <SuspenseWrapper>
            <AgeConfigPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'settings/country-region',
        element: (
          <SuspenseWrapper>
            <CountryRegionPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'settings/location',
        element: (
          <SuspenseWrapper>
            <LocationGovernancePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'settings/feed-ranking',
        element: (
          <SuspenseWrapper>
            <FeedRankingConfigPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'content/liked-places',
        element: (
          <SuspenseWrapper>
            <LikedPlacesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'settings/map-discovery',
        element: (
          <SuspenseWrapper>
            <MapDiscoveryConfigPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'settings/location-likes',
        element: (
          <SuspenseWrapper>
            <LocationLikeConfigPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'settings/check-in',
        element: (
          <SuspenseWrapper>
            <CheckInConfigPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'challenges',
        element: (
          <SuspenseWrapper>
            <ChallengeListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'challenges/create',
        element: (
          <SuspenseWrapper>
            <ChallengeFormPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'challenges/:id/edit',
        element: (
          <SuspenseWrapper>
            <ChallengeFormPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'challenges/:id/submissions',
        element: (
          <SuspenseWrapper>
            <SubmissionListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'challenges/:id',
        element: (
          <SuspenseWrapper>
            <ChallengeDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'challenges/locations',
        element: (
          <SuspenseWrapper>
            <GpsLocationListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'banners',
        element: (
          <Can
            permission={PERMISSIONS.BANNER_READ}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <SuspenseWrapper>
              <BannerListPage />
            </SuspenseWrapper>
          </Can>
        ),
      },
      {
        path: 'referrals',
        element: (
          <Can
            permission={PERMISSIONS.REFERRAL_READ}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <SuspenseWrapper>
              <ReferralCodeListPage />
            </SuspenseWrapper>
          </Can>
        ),
      },
      {
        path: 'referrals/:referralCodeId',
        element: (
          <Can
            permission={PERMISSIONS.REFERRAL_READ}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <SuspenseWrapper>
              <ReferralCodeDetailPage />
            </SuspenseWrapper>
          </Can>
        ),
      },
      {
        path: 'challenges/wallet',
        element: (
          <SuspenseWrapper>
            <WalletOverviewPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'challenges/bank-accounts',
        element: (
          <SuspenseWrapper>
            <BankAccountListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'challenges/payouts',
        element: (
          <SuspenseWrapper>
            <PayoutListPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'challenges/transactions',
        element: (
          <SuspenseWrapper>
            <TransactionListPage />
          </SuspenseWrapper>
        ),
      },
      {
        // Local API testing harness — not permission-gated, because it
        // authenticates with its own test user token rather than the admin
        // session, so admin permissions say nothing about what it can see.
        path: 'map-lab',
        element: (
          <SuspenseWrapper>
            <MapLabPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: '/policy/:type',
    element: (
      <SuspenseWrapper>
        <PolicyPreviewPage />
      </SuspenseWrapper>
    ),
  },
  { path: '*', element: <Navigate to="/dashboard" replace /> },
]);
